import {
  Box,
  Node,
  NodeChange,
  NodePositionChange,
  useNodesInitialized,
  useReactFlow,
  useStoreApi,
} from "@xyflow/react";
import { nodeToBox } from "@xyflow/system";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ANCHORS,
  GUIDE_OVERHANG,
  MAX_GUIDE_NODE_DISTANCE,
  SNAP_RADIUS,
} from "./config";
import { HelperLinesRenderer } from "./HelperLinesRenderer";
import {
  cleanCoord,
  parentOffsetOf,
  quantizeMultiPositions,
  quantizeResizeChanges,
  quantizeSinglePosition,
} from "./quantize";
import { HelperLine } from "./types";
import {
  buildHelperLines,
  getHelperLines,
  snapToHelperLines,
  SpatialIndex,
} from "./utils";

/**
 * Alignment snapping (lattice ruling A5): while a single node is dragged,
 * its edges and centers are compared against every other on-screen node's
 * anchors; within SNAP_RADIUS the guide wins over the 16-grid, letting a
 * node land off-lattice to line up with a neighbor. A plain re-drag with no
 * guide in range re-grids it. Holding Meta/Ctrl bypasses alignment.
 *
 * This interceptor is ALSO the canvas's only lattice quantizer: React
 * Flow's `snapToGrid` is off (it would round positions upstream, before the
 * guide competition can see sub-cell alignment stops), so every
 * pointer-driven change — drags, multi-drags, keyboard nudges, live resizes
 * of symmetric types — is quantized here via `quantize.ts` when no guide
 * claims it.
 *
 * Move-only alignment for now: resize changes are quantized but never
 * guide-captured, because resize release settles dimensions onto the
 * lattice (onResizeEnd), which would immediately destroy an
 * alignment-landed edge. Resize alignment lands with the M7 interaction
 * pass alongside anchor rounding.
 *
 * `enabled: false` (the toolbar Guides toggle) disables the guide
 * competition only — quantization is structural and always on.
 */
export function useHelperLines({ enabled = true }: { enabled?: boolean } = {}) {
  const [helperLineHorizontal, setHelperLineHorizontal] = useState<
    HelperLine | undefined
  >(undefined);
  const [helperLineVertical, setHelperLineVertical] = useState<
    HelperLine | undefined
  >(undefined);

  // Imperative store access only: updateHelperLines runs inside change
  // events, never during render, so subscribing the host component (the
  // whole CanvasPage) to transform/width/height would re-render it on
  // every pan/zoom frame for nothing.
  const storeApi = useStoreApi();

  const nodesInitialized = useNodesInitialized();
  const { getNodes, getInternalNode } = useReactFlow();

  const spatialIndexRef = useRef<SpatialIndex>(new SpatialIndex());

  // Meta/Ctrl temporarily disables alignment so users can drop a node on the
  // bare grid right next to an alignment guide.
  const bypassRef = useRef(false);
  useEffect(() => {
    const sync = (event: KeyboardEvent) => {
      bypassRef.current = event.metaKey || event.ctrlKey;
    };
    const clear = () => {
      bypassRef.current = false;
    };
    window.addEventListener("keydown", sync);
    window.addEventListener("keyup", sync);
    window.addEventListener("blur", clear);
    return () => {
      window.removeEventListener("keydown", sync);
      window.removeEventListener("keyup", sync);
      window.removeEventListener("blur", clear);
    };
  }, []);

  const resetHelperLines = useCallback(() => {
    setHelperLineHorizontal(undefined);
    setHelperLineVertical(undefined);
  }, []);

  const rebuildIndex = useCallback(
    (nodes: Node[]) => {
      resetHelperLines();
      // Connectors aren't visible rectangles — their anchors would produce
      // phantom guides.
      const guideSources = nodes.filter(
        (node) =>
          !node.hidden &&
          node.type !== "canvasConnector" &&
          node.type !== "canvasDrawing",
      );
      const helperLines = buildHelperLines(
        guideSources,
        ANCHORS,
        getInternalNode,
      );
      spatialIndexRef.current.initialize(helperLines);
    },
    [getInternalNode, resetHelperLines],
  );

  useEffect(() => {
    if (nodesInitialized) {
      rebuildIndex(getNodes());
    }
  }, [nodesInitialized, rebuildIndex, getNodes]);

  // Distinguishes drag-release changes (dragging:false after a dragging:true
  // stream) from keyboard nudges (dragging:false out of nowhere) — both
  // arrive with the same shape, but nudges need the one-cell bump.
  const dragActiveRef = useRef(false);

  const updateHelperLines = useCallback(
    (changes: NodeChange[], nodes: Node[]): NodeChange[] => {
      // ── The interceptor owns ALL lattice quantization. React Flow's own
      // snapToGrid is off (it would round positions before the guide
      // competition can see them); every pointer-driven change is either
      // claimed by a guide or quantized here. Programmatic changes
      // (selection, add/remove, layout writes) pass through untouched.

      // Resize stream: dimension changes + their paired origin positions.
      if (changes.some((c) => c.type === "dimensions")) {
        resetHelperLines();
        return quantizeResizeChanges(changes, getInternalNode);
      }

      const dragChanges = changes.filter(
        (c): c is NodePositionChange =>
          c.type === "position" && "dragging" in c && !!c.position,
      );
      if (dragChanges.length === 0) {
        resetHelperLines();
        return changes;
      }

      const anyDragging = dragChanges.some((c) => c.dragging);
      const keyboardLike = !anyDragging && !dragActiveRef.current;
      // First dragging event of a gesture must always be forwarded — the
      // Liveblocks adapter pairs history.pause() on it with resume() on the
      // release — so no-op suppression below only kicks in mid-gesture.
      const dragStart = anyDragging && !dragActiveRef.current;
      dragActiveRef.current = anyDragging;

      // Multi-selection drag / multi-node nudge: lead-node shared-offset
      // quantization; alignment guides don't compete (deliberate offsets
      // inside the selection must survive).
      if (dragChanges.length > 1) {
        resetHelperLines();
        const multi = quantizeMultiPositions(
          changes,
          getInternalNode,
          keyboardLike,
        );
        // With RF's snapToGrid off, drag events arrive at pointer rate; the
        // upstream no-op suppression that capped event volume at lattice
        // crossings is gone. Re-create it here: mid-gesture events whose
        // quantized positions all repeat the current ones carry no
        // information — forwarding them costs a Liveblocks mutation +
        // React work per pointer event.
        if (
          anyDragging &&
          !dragStart &&
          multi.every((c) => {
            if (c.type !== "position" || !("dragging" in c) || !c.position) {
              return true;
            }
            const internal = getInternalNode(c.id);
            return (
              !!internal &&
              c.position.x === internal.position.x &&
              c.position.y === internal.position.y
            );
          })
        ) {
          return [];
        }
        return multi;
      }

      const positionChange = dragChanges[0];
      const node = nodes.find(
        (candidate) => candidate.id === positionChange.id,
      );
      const internalNode = node ? getInternalNode(node.id) : undefined;
      if (!node || !internalNode) {
        resetHelperLines();
        return changes;
      }

      const quantized = quantizeSinglePosition(
        positionChange,
        internalNode,
        keyboardLike,
      );

      // Mid-gesture no-op suppression (see the multi-drag comment): the
      // final emitted position hasn't moved off the node's current one.
      const dropNoOp = (pos: { x: number; y: number } | null | undefined) =>
        positionChange.dragging === true &&
        !dragStart &&
        !!pos &&
        pos.x === internalNode.position.x &&
        pos.y === internalNode.position.y;

      if (!enabled || bypassRef.current || !quantized.position) {
        resetHelperLines();
        return dropNoOp(quantized.position) ? [] : [quantized];
      }

      resetHelperLines();

      // Guides compete on the RAW position — sub-cell resolution is the
      // point. Alignment stops often sit between lattice rows (centers of
      // near-equal heights are half the height delta from an edge stop);
      // only the raw pointer-driven position can reach them. The grid is
      // the fallback for whatever no guide claims.
      //
      // Relative↔absolute uses the node's OWN internals-derived offset
      // (parentOffsetOf) — correct at any section nesting depth; a
      // parent's `.position` is itself relative and must not be an origin.
      const rawPosition = positionChange.position!;
      const offset = parentOffsetOf(internalNode);
      const absX = rawPosition.x + offset.x;
      const absY = rawPosition.y + offset.y;
      const nodeBounds: Box = nodeToBox(internalNode);
      nodeBounds.x2 = absX + (nodeBounds.x2 - nodeBounds.x);
      nodeBounds.y2 = absY + (nodeBounds.y2 - nodeBounds.y);
      nodeBounds.x = absX;
      nodeBounds.y = absY;

      // Visible flow-space box, derived from the pane transform directly.
      // (The vendored example fed window-client coords to
      // screenToFlowPosition, which is only correct when the pane sits at
      // the window origin — ours is offset by the rail/sidebar.)
      const { width, height, transform } = storeApi.getState();
      const [tx, ty, tz] = transform;
      const viewportBox: Box = {
        x: (0 - tx) / tz,
        y: (0 - ty) / tz,
        x2: (width - tx) / tz,
        y2: (height - ty) / tz,
      };

      // Screen-constant feel: both the neighbor reach and the capture
      // radius are defined in screen px and divided by zoom — zoomed out,
      // guides reach and grab across proportionally more flow space.
      const snapRadius = SNAP_RADIUS / tz;
      const { horizontal: hMatch, vertical: vMatch } = getHelperLines(
        spatialIndexRef.current,
        viewportBox,
        node,
        nodeBounds,
        MAX_GUIDE_NODE_DISTANCE / tz,
        snapRadius,
      );

      // Work on a copy so the original change is returned untouched when no
      // guide captures. The copy holds ABSOLUTE coordinates (guides are
      // absolute); for children of sections the original change is
      // parent-relative, so convert going in — snapToHelperLines converts
      // back on the way out.
      const snapChange: NodePositionChange = {
        id: node.id,
        type: "position",
        position: {
          x: internalNode.internals.positionAbsolute.x,
          y: internalNode.internals.positionAbsolute.y,
        },
        dragging: positionChange.dragging,
      };

      if (snapChange.position) {
        snapChange.position.x = absX;
        snapChange.position.y = absY;
      }

      const { snappedX, snappedY } = snapToHelperLines(
        node,
        internalNode,
        snapChange,
        positionChange,
        undefined,
        undefined,
        hMatch,
        vMatch,
        snapRadius,
      );

      if (snappedX || snappedY) {
        // snapChange.position is still ABSOLUTE here (no parentNode was
        // given to snapToHelperLines) — capture the span box before
        // converting, then convert captured axes to parent-relative and
        // millipixel-cap them (residue from legacy-node guide lines both
        // persists junk and defeats the adapter's write dedupe). Uncaptured
        // axes fall back to the grid-quantized (already relative) value.
        const snappedAbs: Box = {
          x: snapChange.position?.x ?? absX,
          y: snapChange.position?.y ?? absY,
          x2: 0,
          y2: 0,
        };
        snappedAbs.x2 = snappedAbs.x + (node.measured?.width ?? 0);
        snappedAbs.y2 = snappedAbs.y + (node.measured?.height ?? 0);
        if (snapChange.position && quantized.position) {
          snapChange.position.x = snappedX
            ? cleanCoord(snapChange.position.x - offset.x)
            : quantized.position.x;
          snapChange.position.y = snappedY
            ? cleanCoord(snapChange.position.y - offset.y)
            : quantized.position.y;
        }
        // Guides render only mid-drag; the release change still snaps so the
        // aligned position is what persists.
        if (positionChange.dragging) {
          if (snappedX && vMatch) {
            setHelperLineVertical({
              ...vMatch.line,
              span: [
                Math.min(vMatch.line.nodeBox.y, snappedAbs.y) - GUIDE_OVERHANG,
                Math.max(vMatch.line.nodeBox.y2, snappedAbs.y2) +
                  GUIDE_OVERHANG,
              ],
            });
          }
          if (snappedY && hMatch) {
            setHelperLineHorizontal({
              ...hMatch.line,
              span: [
                Math.min(hMatch.line.nodeBox.x, snappedAbs.x) - GUIDE_OVERHANG,
                Math.max(hMatch.line.nodeBox.x2, snappedAbs.x2) +
                  GUIDE_OVERHANG,
              ],
            });
          }
        }
        return dropNoOp(snapChange.position) ? [] : [snapChange];
      }

      return dropNoOp(quantized.position) ? [] : [quantized];
    },
    [enabled, storeApi, getInternalNode, resetHelperLines],
  );

  const HelperLines = useCallback(() => {
    return (
      <HelperLinesRenderer
        horizontal={helperLineHorizontal}
        vertical={helperLineVertical}
      />
    );
  }, [helperLineHorizontal, helperLineVertical]);

  return {
    rebuildIndex,
    updateHelperLines,
    HelperLines,
  };
}
